import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Dialog, DialogContent, Icon } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { formatMoneyPunto } from "utils/formatters";

const formatVoucherDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "-");

const formatVoucherAmount = (value, tipo) => {
  const numericValue = Number(value || 0);
  if (tipo === "MONTO") return `$ ${formatMoneyPunto(numericValue)}`;
  return numericValue.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatVoucherMetricValue = (value, tipo) => {
  const amount = formatVoucherAmount(value, tipo);
  return tipo === "MONTO" ? amount : `${amount} unidades`;
};

const getVoucherDisplayType = (tipo) => {
  if (tipo === "MONTO") return "Monto";
  if (tipo === "PRODUCTO") return "Producto";
  if (tipo === "CATEGORIA") return "Categoría";
  return "Voucher";
};

const getVoucherNoveltyLabel = (tipoNovedad) =>
  tipoNovedad === "MODIFICADO" ? "Actualizado" : "Nuevo";

const getVoucherVisualConfig = (tipo) => {
  if (tipo !== "MONTO") {
    return {
      headerIcon: "local_mall",
      headerIconBg: "linear-gradient(135deg, rgba(255, 146, 194, 0.12), rgba(255, 225, 118, 0.2))",
      headerIconColor: "#F10A7B",
      typePillBg: "linear-gradient(135deg, #FFE6B7 0%, #FFF2D8 100%)",
      typePillColor: "#D88010",
      availablePillBg: "linear-gradient(135deg, #DDF6B7 0%, #EEFAD8 100%)",
      availablePillColor: "#63A91E",
      metrics: {
        total: {
          icon: "inventory_2",
          iconBg: "linear-gradient(135deg, #FFF0E2 0%, #FFF8F1 100%)",
          iconColor: "#F2871D",
          borderColor: "rgba(247, 212, 175, 0.9)",
        },
        consumed: {
          icon: "restaurant",
          iconBg: "linear-gradient(135deg, #F4F6FB 0%, #FCFDFF 100%)",
          iconColor: "#7487B0",
          borderColor: "rgba(221, 228, 240, 0.95)",
        },
        available: {
          icon: "redeem",
          iconBg: "linear-gradient(135deg, #EDF9D8 0%, #F7FDEB 100%)",
          iconColor: "#59B31A",
          borderColor: "rgba(210, 236, 173, 0.95)",
        },
      },
    };
  }

  return {
    headerIcon: "person_outline",
    headerIconBg: "linear-gradient(135deg, #FFF0F7 0%, #FFF7FB 100%)",
    headerIconColor: "#F10A7B",
    typePillBg: "linear-gradient(135deg, #DFF3FF 0%, #EFFAFF 100%)",
    typePillColor: "#1691DA",
    availablePillBg: "linear-gradient(135deg, #DDF6B7 0%, #EEFAD8 100%)",
    availablePillColor: "#63A91E",
    metrics: {
      total: {
        icon: "account_balance_wallet",
        iconBg: "linear-gradient(135deg, #E4F2FF 0%, #F2F8FF 100%)",
        iconColor: "#2683EF",
        borderColor: "rgba(205, 226, 248, 0.95)",
      },
      consumed: {
        icon: "shopping_cart",
        iconBg: "linear-gradient(135deg, #F4F6FB 0%, #FCFDFF 100%)",
        iconColor: "#7487B0",
        borderColor: "rgba(221, 228, 240, 0.95)",
      },
      available: {
        icon: "account_balance_wallet",
        iconBg: "linear-gradient(135deg, #EDF9D8 0%, #F7FDEB 100%)",
        iconColor: "#59B31A",
        borderColor: "rgba(210, 236, 173, 0.95)",
      },
    },
  };
};

const confettiPieces = [
  { top: 30, left: 42, width: 10, height: 24, rotate: "-42deg", background: "linear-gradient(180deg, #FFA6D1, #FF77B6)" },
  { top: 78, left: 52, width: 8, height: 18, rotate: "-72deg", background: "linear-gradient(180deg, #B5DBFF, #87C0FF)" },
  { top: 32, right: 44, width: 10, height: 24, rotate: "36deg", background: "linear-gradient(180deg, #FFA6D1, #FF77B6)" },
  { top: 78, right: 58, width: 8, height: 18, rotate: "48deg", background: "linear-gradient(180deg, #B5DBFF, #87C0FF)" },
  { top: 64, left: "28%", width: 8, height: 18, rotate: "54deg", background: "linear-gradient(180deg, #D8F38E, #BFE25D)" },
  { top: 64, right: "28%", width: 8, height: 18, rotate: "-18deg", background: "linear-gradient(180deg, #D8F38E, #BFE25D)" },
];

const sparklePieces = [
  { top: 18, left: "22%", size: 16 },
  { top: 18, right: "22%", size: 16 },
  { top: 46, left: "34%", size: 14 },
  { top: 48, right: "34%", size: 14 },
  { top: 70, right: "14%", size: 12 },
];

function VoucherPill({ label, background, color }) {
  return (
    <SoftBox
      sx={{
        height: 28,
        px: 1.45,
        borderRadius: "999px",
        background,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <SoftTypography
        sx={{
          color,
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: 0.2,
          lineHeight: 1,
        }}
      >
        {label}
      </SoftTypography>
    </SoftBox>
  );
}

VoucherPill.propTypes = {
  label: PropTypes.string.isRequired,
  background: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

function VoucherMetricRow({ label, value, icon }) {
  return (
    <SoftBox
      sx={{
        minHeight: 56,
        px: 1.1,
        py: 0.7,
        borderRadius: "14px",
        border: "1px solid rgba(226, 232, 240, 0.96)",
        backgroundColor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <SoftBox
        sx={{
          width: 32,
          height: 32,
          minWidth: 32,
          borderRadius: "50%",
          background: "#F1F5F9",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Icon sx={{ color: "#64748B", fontSize: "1rem !important" }}>{icon}</Icon>
      </SoftBox>
      <SoftBox minWidth={0}>
        <SoftTypography sx={{ color: "#94A3B8", fontSize: "0.72rem", lineHeight: 1.1, mb: 0.25 }}>
          {label}
        </SoftTypography>
        <SoftTypography sx={{ color: "#172554", fontSize: "0.86rem", fontWeight: 700, lineHeight: 1.1 }}>
          {value}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

VoucherMetricRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
};
function renderHeroMessage(vouchers) {
  const firstVoucher = vouchers?.[0];
  if (!firstVoucher) return null;

  if (vouchers.length === 1 && firstVoucher.tipo === "MONTO") {
    return (
      <SoftTypography
        sx={{
          color: "#0A2A6C",
          fontWeight: 700,
          fontSize: { xs: "0.98rem", sm: "1.06rem" },
          lineHeight: 1.25,
          textAlign: "center",
        }}
      >
        Has recibido un voucher de <SoftBox component="span" sx={{ color: "#F0067D" }}>{formatVoucherAmount(firstVoucher.cantidad, firstVoucher.tipo)}</SoftBox>
      </SoftTypography>
    );
  }

  const destino = firstVoucher?.tipo === "PRODUCTO"
    ? firstVoucher?.producto?.nombre
    : firstVoucher?.categoria?.categoria;
  return (
    <SoftTypography
      sx={{
        color: "#0A2A6C",
        fontWeight: 700,
        fontSize: { xs: "0.98rem", sm: "1.06rem" },
        lineHeight: 1.25,
        textAlign: "center",
      }}
    >
      Has recibido el voucher <SoftBox component="span" sx={{ color: "#F0067D" }}>{destino || "beneficio"}</SoftBox>
    </SoftTypography>
  );
}

function DialogVouchersActivos({ open, onAcknowledge, vouchers, acknowledging, errorMessage }) {
  const displayVouchers = [...(vouchers || [])].sort((first, second) => {
    if (first?.notificacionPendiente === second?.notificacionPendiente) return 0;
    return first?.notificacionPendiente ? -1 : 1;
  });
  const hasManyVouchers = displayVouchers.length > 1;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="voucher-benefit-title"
      aria-describedby="voucher-benefit-description"
      fullWidth
      maxWidth={false}
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          width: "min(430px, calc(100vw - 24px))",
          maxWidth: "430px",
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
          m: 1.5,
        },
      }}
    >
      <DialogContent sx={{ p: 0, overflow: "visible" }}>
        <SoftBox
          sx={{
            background: "linear-gradient(180deg, #FF0A84 0%, #E3006D 100%)",
            borderRadius: "30px",
            p: "10px",
            boxShadow: "0 22px 58px rgba(227, 0, 109, 0.22)",
          }}
        >
          <SoftBox
            sx={{
              position: "relative",
              borderRadius: "24px",
              backgroundColor: "#FFFFFF",
              px: 2.15,
              pt: 4,
              pb: 2.15,
              overflow: "hidden",
            }}
          >
            {confettiPieces.map((piece, index) => (
              <SoftBox
                key={`confetti-${index}`}
                sx={{
                  position: "absolute",
                  top: piece.top,
                  left: piece.left,
                  right: piece.right,
                  width: piece.width,
                  height: piece.height,
                  borderRadius: "999px",
                  background: piece.background,
                  transform: `rotate(${piece.rotate})`,
                }}
              />
            ))}
            {sparklePieces.map((piece, index) => (
              <Icon
                key={`sparkle-${index}`}
                sx={{
                  position: "absolute",
                  top: piece.top,
                  left: piece.left,
                  right: piece.right,
                  fontSize: `${piece.size}px !important`,
                  color: "#FF82BC",
                }}
              >
                auto_awesome
              </Icon>
            ))}

            <SoftBox display="flex" justifyContent="center" mb={1.7}>
              <SoftBox
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  background: "radial-gradient(circle at center, rgba(255, 241, 247, 1) 0%, rgba(255, 247, 250, 1) 100%)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Icon sx={{ color: "#F0067D", fontSize: "3.25rem !important" }}>confirmation_number</Icon>
              </SoftBox>
            </SoftBox>

            <SoftTypography
              id="voucher-benefit-title"
              component="h2"
              sx={{
                textAlign: "center",
                color: "#F0067D",
                fontWeight: 700,
                fontSize: "2.15rem",
                lineHeight: 1,
                letterSpacing: "-0.03em",
                mb: 1.25,
              }}
            >
              &iexcl;Beneficio para ti!
            </SoftTypography>

            <SoftBox sx={{ maxWidth: 320, mx: "auto", mb: 0.65 }}>
              {renderHeroMessage(displayVouchers)}
            </SoftBox>

            <SoftTypography
              id="voucher-benefit-description"
              sx={{
                textAlign: "center",
                color: "#6F82AE",
                fontWeight: 400,
                fontSize: "0.98rem",
                lineHeight: 1.2,
                mb: 2.15,
              }}
            >
              &Uacute;salo en tu pr&oacute;xima compra
            </SoftTypography>

            <SoftBox
              sx={{
                border: "1px solid rgba(225, 231, 241, 0.96)",
                borderRadius: "20px",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)",
                px: 1.2,
                py: 1.2,
              }}
            >
              <SoftBox
                sx={{
                  display: "grid",
                  gap: 1.3,
                  maxHeight: hasManyVouchers ? "42vh" : "none",
                  overflowY: hasManyVouchers ? "auto" : "visible",
                  pr: hasManyVouchers ? 0.4 : 0,
                  "&::-webkit-scrollbar": {
                    width: hasManyVouchers ? 6 : 0,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(160, 174, 192, 0.65)",
                    borderRadius: "999px",
                  },
                }}
              >
                {displayVouchers.map((voucher, index) => {
                  const visual = getVoucherVisualConfig(voucher?.tipo);
                  const nombreVoucher = voucher?.nombreVoucher || voucher?.grupo?.nombre || "Voucher activo";
                  const isNovelty = Boolean(voucher?.notificacionPendiente);

                  return (
                    <SoftBox
                      key={voucher?.id || index}
                      sx={{
                        border: isNovelty
                          ? "2px solid rgba(240, 6, 125, 0.72)"
                          : "1px solid rgba(228, 233, 242, 0.96)",
                        borderRadius: "18px",
                        background: isNovelty
                          ? "linear-gradient(180deg, #FFF7FB 0%, #FFFFFF 48%)"
                          : "#FFFFFF",
                        boxShadow: isNovelty ? "0 10px 24px rgba(227, 0, 109, 0.1)" : "none",
                        px: 1.35,
                        py: 1.2,
                      }}
                    >
                      <SoftBox mb={1.2}>
                        <SoftTypography
                          sx={{
                            color: "#172554",
                            fontWeight: 700,
                            fontSize: "0.86rem",
                            lineHeight: 1.25,
                            width: "100%",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {nombreVoucher}
                        </SoftTypography>
                        <SoftBox
                          display="flex"
                          alignItems="center"
                          gap={0.65}
                          flexWrap="wrap"
                          mt={0.85}
                        >
                          {isNovelty ? (
                            <VoucherPill
                              label={getVoucherNoveltyLabel(voucher?.tipoNovedad)}
                              background="linear-gradient(135deg, #FFE1F0 0%, #FFF1F8 100%)"
                              color="#E3006D"
                            />
                          ) : null}
                          <VoucherPill label="Disponible" background={visual.availablePillBg} color={visual.availablePillColor} />
                        </SoftBox>
                      </SoftBox>

                      <SoftBox display="grid" gap={0.9}>
                        <VoucherMetricRow label="Disponible" value={formatVoucherMetricValue(voucher?.saldo, voucher?.tipo)} icon="account_balance_wallet" />
                        <VoucherMetricRow label="Limite diario" value={formatVoucherMetricValue(voucher?.limiteDiario, voucher?.tipo)} icon="event_available" />
                      </SoftBox>

                      {voucher?.tipo !== "MONTO" ? (
                        <SoftTypography sx={{ display: "block", mt: 1, color: "#64748B", fontSize: "0.76rem", lineHeight: 1.2, fontWeight: 600 }}>
                          {getVoucherDisplayType(voucher.tipo)}: {voucher.tipo === "PRODUCTO" ? voucher?.producto?.nombre || "-" : voucher?.categoria?.categoria || "-"}
                        </SoftTypography>
                      ) : null}

                      <SoftTypography sx={{ display: "block", mt: 1, color: "#94A3B8", fontSize: "0.72rem", lineHeight: 1.2 }}>
                        Vigencia: {formatVoucherDate(voucher?.desde)} al {formatVoucherDate(voucher?.hasta)}
                      </SoftTypography>
                      <SoftTypography sx={{ display: "block", mt: 0.7, color: "#E3006D", fontSize: "0.74rem", lineHeight: 1.2, fontWeight: 700 }}>
                        {voucher?.grupo?.sede?.nombre ? `Canjeable en ${voucher.grupo.sede.nombre}` : "Canjeable en todas las sedes"}
                      </SoftTypography>
                    </SoftBox>
                  );
                })}
              </SoftBox>
            </SoftBox>

            {errorMessage ? (
              <SoftTypography
                role="alert"
                sx={{ color: "#C62828", fontSize: "0.76rem", textAlign: "center", mt: 1.35 }}
              >
                {errorMessage}
              </SoftTypography>
            ) : null}

            <SoftBox display="flex" justifyContent="center" mt={2.05}>
              <SoftButton
                onClick={onAcknowledge}
                disabled={acknowledging}
                sx={{
                  minWidth: 210,
                  height: 54,
                  borderRadius: "999px",
                  background: "linear-gradient(180deg, #FF0A84 0%, #E3006D 100%) !important",
                  color: "#FFFFFF !important",
                  fontWeight: 700,
                  fontSize: "1.08rem",
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                  boxShadow: "0 16px 34px rgba(227, 0, 109, 0.24)",
                  px: 4,
                  '&:hover': {
                    background: "linear-gradient(180deg, #FF0A84 0%, #D10065 100%) !important",
                  },
                }}
              >
                {acknowledging ? "GUARDANDO..." : "ENTENDIDO"}
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </DialogContent>
    </Dialog>
  );
}

DialogVouchersActivos.propTypes = {
  open: PropTypes.bool.isRequired,
  onAcknowledge: PropTypes.func.isRequired,
  acknowledging: PropTypes.bool,
  errorMessage: PropTypes.string,
  vouchers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      nombreVoucher: PropTypes.string,
      desde: PropTypes.string,
      hasta: PropTypes.string,
      tipo: PropTypes.string,
      cantidad: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      consumido: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      saldo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      versionNotificacion: PropTypes.string,
      notificacionPendiente: PropTypes.bool,
      tipoNovedad: PropTypes.string,
      categoria: PropTypes.shape({
        categoria: PropTypes.string,
      }),
      producto: PropTypes.shape({
        nombre: PropTypes.string,
      }),
      grupo: PropTypes.shape({
        nombre: PropTypes.string,
        sede: PropTypes.shape({ nombre: PropTypes.string }),
      }),
    })
  ),
};

DialogVouchersActivos.defaultProps = {
  vouchers: [],
  acknowledging: false,
  errorMessage: "",
};

export default DialogVouchersActivos;
